import React, { useState, useEffect } from 'react';

const CaroBoard = ({ 
  gameState, 
  playerColor, 
  currentTurn, 
  onMove, 
  gameStatus, 
  boardSize = 15,
  winCondition = 5,
  lastMove = null  // { row, col } of the last move
}) => {
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Create empty board if gameState is not available
  const board = gameState || Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));

  const canMakeMove = () => {
    return !gameStatus.isGameOver && 
           currentTurn === playerColor && 
           gameState !== null;
  };

  const canSelectSquare = (row, col) => {
    return canMakeMove() && board[row][col] === null;
  };

  const handleSquareClick = (row, col) => {
    if (!canSelectSquare(row, col)) return;
    onMove(row, col);
  };

  const handleSquareHover = (row, col) => {
    if (canSelectSquare(row, col)) {
      setHoveredSquare({ row, col });
    }
  };

  const handleSquareLeave = () => {
    setHoveredSquare(null);
  };

  // Touch events for mobile
  const handleTouchStart = (row, col) => {
    if (isMobile && canSelectSquare(row, col)) {
      setHoveredSquare({ row, col });
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      setHoveredSquare(null);
    }
  };

  const isWinningSquare = (row, col) => {
    if (!gameStatus.winningLine) return false;
    return gameStatus.winningLine.some(([r, c]) => r === row && c === col);
  };

  const isLastMoveSquare = (row, col) => {
    if (!lastMove) return false;
    return lastMove.row === row && lastMove.col === col;
  };

  const getLastMovePlayer = (row, col) => {
    if (!lastMove || !board || !isLastMoveSquare(row, col)) return null;
    return board[row][col];
  };

  const getSquareClasses = (row, col) => {
    let classes = ['caro-square'];
    
    if (isWinningSquare(row, col)) {
      classes.push('winning-square');
    }
    
    if (isLastMoveSquare(row, col)) {
      const lastMovePlayer = getLastMovePlayer(row, col);
      classes.push('last-move');
      if (lastMovePlayer) {
        classes.push(`last-move-${lastMovePlayer.toLowerCase()}`);
      }
    }
    
    if (canSelectSquare(row, col)) {
      classes.push('playable');
    }
    
    if (hoveredSquare && hoveredSquare.row === row && hoveredSquare.col === col) {
      classes.push('hovered');
    }
    
    return classes.join(' ');
  };

  // Calculate responsive square size
  const getSquareSize = () => {
    if (isMobile) {
      const screenWidth = window.innerWidth;
      const maxBoardWidth = screenWidth - 40; // 20px padding on each side
      const squareSize = Math.min(30, Math.floor(maxBoardWidth / boardSize));
      return Math.max(20, squareSize); // Minimum 20px for touch targets
    }
    return 30; // Default desktop size
  };

  const renderSquare = (row, col) => {
    const piece = board[row][col];
    const isHovered = hoveredSquare && hoveredSquare.row === row && hoveredSquare.col === col;
    const squareSize = getSquareSize();
    
    return (
      <div
        key={`${row}-${col}`}
        className={getSquareClasses(row, col)}
        onClick={() => handleSquareClick(row, col)}
        onMouseEnter={() => !isMobile && handleSquareHover(row, col)}
        onMouseLeave={() => !isMobile && handleSquareLeave()}
        onTouchStart={() => handleTouchStart(row, col)}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: canSelectSquare(row, col) ? 'pointer' : 'default',
          width: `${squareSize}px`,
          height: `${squareSize}px`,
          fontSize: `${squareSize * 0.6}px`
        }}
      >
        {piece && (
          <span className={`piece piece-${piece.toLowerCase()}`}>
            {piece}
          </span>
        )}
        {isHovered && canSelectSquare(row, col) && (
          <span className={`piece piece-preview piece-${playerColor?.toLowerCase()}`}>
            {playerColor}
          </span>
        )}
      </div>
    );
  };

  const renderBoard = () => {
    const squares = [];
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        squares.push(renderSquare(row, col));
      }
    }
    return squares;
  };

  const getGameStatusText = () => {
    if (gameStatus.isGameOver) {
      if (gameStatus.winner) {
        return gameStatus.winner === playerColor ? 'You win! 🎉' : 'You lose 😔';
      } else {
        return 'Draw! 🤝';
      }
    }
    
    if (!gameState) {
      return 'Waiting for game to start...';
    }
    
    return currentTurn === playerColor ? 'Your turn' : `${currentTurn}'s turn`;
  };

  return (
    <div className="caro-board-container">
      <div className="board-info">
        <div className="game-info">
          <span className="board-size">Board: {boardSize}×{boardSize}</span>
          <span className="win-condition">Win: {winCondition} in a row</span>
          <span className={`current-turn ${gameStatus.isGameOver ? 'game-over' : ''}`}>
            {getGameStatusText()}
          </span>
        </div>
      </div>
      
      <div 
        className="caro-board"
        style={{
          gridTemplateColumns: `repeat(${boardSize}, 1fr)`,
          gridTemplateRows: `repeat(${boardSize}, 1fr)`,
          maxWidth: isMobile ? '95vw' : Math.min(600, boardSize * 30),
          maxHeight: isMobile ? '70vh' : Math.min(600, boardSize * 30),
          overflow: isMobile ? 'auto' : 'visible'
        }}
      >
        {!gameState ? (
          <div className="board-placeholder">
            <div>Waiting for game to start...</div>
          </div>
        ) : (
          renderBoard()
        )}
      </div>
      
      {gameStatus.isGameOver && (
        <div className="game-end-info">
          <h3>Game Over!</h3>
          {gameStatus.winner ? (
            <p>
              <span className={`piece piece-${gameStatus.winner.toLowerCase()}`}>
                {gameStatus.winner}
              </span> wins!
            </p>
          ) : (
            <p>It's a draw!</p>
          )}
          {gameStatus.winningLine && (
            <p>Winning line: {gameStatus.winningLine.length} pieces in a row</p>
          )}
          {gameStatus.reason && (
            <p>Reason: {gameStatus.reason}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CaroBoard;
